import { createSelector } from '@reduxjs/toolkit';
import sum from 'lodash/sum';
import type { RootState } from 'src/root/store';

import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { pendingRequestsCountSel } from '@offline';
import {
  allNotAwardedAdvancementsCountSel,
  pendingApprovableAdvancementsSel,
} from '@shared/duck';

import { moduleName } from '../constants';
import { NotificationsState } from '../types';

export {
  pendingApprovableAdvancementsSel as pendingAdvancementsSel,
  allNotAwardedAdvancementsCountSel,
};

const moduleSel = (state: RootState) => state[moduleName] as NotificationsState;

export const notificationsVisibleSel = (state: RootState) =>
  moduleSel(state).notificationsVisible;

export const pendingAdvancementsCountSel = (state: RootState) =>
  pendingApprovableAdvancementsSel(state).length;

export const badgeCountSel = createSelector(
  pendingAdvancementsCountSel,
  allNotAwardedAdvancementsCountSel,
  pendingRequestsCountSel,
  (
    pendingAdvancementsCount,
    notAwardedAdvancementsCount,
    pendingRequestsCount,
  ) =>
    pendingAdvancementsCount +
    notAwardedAdvancementsCount +
    pendingRequestsCount,
);

export const notificationCountsSel = (state: RootState) => {
  const moduleState = state.userNotifications as NotificationsState;
  const requirementsCount = featureFlags.getFlag(
    'SBL_4724_PENDING_REQUIREMENTS',
  )
    ? sum(
        Object.values(
          moduleState.dashboardStats?.completed?.requirements || {},
        ),
      )
    : 0;

  return {
    totalCounts: moduleState.totalCount,
    toApproveCount: moduleState.toApproveCount,
    toPurchaseCount: moduleState.toPurchaseCount,
    ranksCount: moduleState.dashboardStats?.completed?.ranks || 0,
    adventuresCount: moduleState.dashboardStats?.completed?.adventures || 0,
    meritBadgesCount: moduleState.dashboardStats?.completed?.meritBadges || 0,
    awardsCount: moduleState.dashboardStats?.completed?.awards || 0,
    requirementsCount,
  };
};

export const toPurchaseCountSel = createSelector(
  notificationCountsSel,
  counts => counts.toPurchaseCount,
);

export const hasNotificationsToShowSel = (state: NotificationsState) =>
  badgeCountSel(state) > 0;
